import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { leadService, leadSourceService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, Search, Loader2 } from "lucide-react";

export const LEAD_STATUS_COLOR = { New: "blue", Contacted: "amber", Qualified: "teal", Disqualified: "red", Converted: "green" };
const STATUSES = ["New", "Contacted", "Qualified", "Disqualified", "Converted"];
const RATINGS = ["Hot", "Warm", "Cold"];

const EMPTY = {
  firstName: "", lastName: "", company: "", title: "", email: "", mobile: "",
  leadSourceId: "", status: "New", rating: "Warm", estimatedValue: "", description: "", tenantId: "",
};

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-foreground mb-1">{label}</label>{children}</div>;
}

export default function LeadsPage() {
  const { hasPermission, user } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sources, setSources] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await leadService.list({ q: q || undefined, status: statusFilter || undefined, size: 100 });
      setRows(d.items || []);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { leadSourceService.list({ size: 200 }).then((d) => setSources(d.items || [])).catch(() => {}); }, []);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...EMPTY, ...row, estimatedValue: row.estimatedValue ?? "" }); setOpen(true); };

  const save = async () => {
    if (!form.firstName && !form.company) return toast.error("First name or company is required");
    if (isSuper && !editing && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const payload = { ...form, estimatedValue: form.estimatedValue === "" ? null : Number(form.estimatedValue) };
      if (editing) {
        delete payload.tenantId;
        await leadService.update(editing.id, payload);
        toast.success("Lead updated");
      } else {
        const clean = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== "" && v !== null));
        clean.tenantId = form.tenantId || undefined;
        await leadService.create(clean);
        toast.success("Lead created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete lead "${row.fullName}"?`)) return;
    try { await leadService.remove(row.id); toast.success("Lead deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canCreate = hasPermission("LEAD.CREATE");
  const canEdit = hasPermission("LEAD.EDIT");
  const canDelete = hasPermission("LEAD.DELETE");

  const columns = [
    { key: "leadCode", header: "Code", render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.leadCode}</span> },
    { key: "fullName", header: "Name", render: (r) => <span className="font-medium text-foreground">{r.fullName}</span> },
    { key: "company", header: "Company", render: (r) => r.company || "—" },
    { key: "status", header: "Status", render: (r) => <Badge color={LEAD_STATUS_COLOR[r.status] || "slate"}>{r.status}</Badge> },
    { key: "rating", header: "Rating", render: (r) => <Badge color={r.rating === "Hot" ? "red" : r.rating === "Cold" ? "slate" : "amber"}>{r.rating}</Badge> },
    { key: "score", header: "Score", render: (r) => <span className="text-muted-foreground">{r.score ?? 0}</span> },
    {
      key: "_actions", header: "",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => navigate(`/admin/leads/${r.id}`)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`lead-view-${r.id}`}><Eye className="h-4 w-4" /></button>
          {canEdit && r.status !== "Converted" && <button onClick={() => openEdit(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`lead-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>}
          {canDelete && <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`lead-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="leads-page">
      <PageHeader title="Leads" subtitle="Capture, qualify and convert prospects into customers"
        actions={canCreate && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="lead-create-btn">
            <Plus className="h-4 w-4" /> New Lead
          </button>
        )}
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2 w-64">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, company, email…" className="bg-transparent text-sm outline-none w-full text-foreground" data-testid="lead-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="lead-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <DataTable columns={columns} rows={rows} loading={loading} testid="leads-table" empty="No leads yet" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="lead-dialog">
            <h3 className="text-lg font-bold mb-4">{editing ? "Edit" : "New"} Lead</h3>
            <div className="space-y-3.5">
              {isSuper && !editing && (
                <Field label="Tenant *">
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} className="ciq-input" data-testid="lead-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </Field>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="First Name"><input value={form.firstName || ""} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="ciq-input" data-testid="lead-firstName" /></Field>
                <Field label="Last Name"><input value={form.lastName || ""} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="ciq-input" data-testid="lead-lastName" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Company"><input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} className="ciq-input" data-testid="lead-company" /></Field>
                <Field label="Title"><input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="ciq-input" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email"><input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ciq-input" data-testid="lead-email" /></Field>
                <Field label="Mobile"><input value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="ciq-input" /></Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Source">
                  <select value={form.leadSourceId || ""} onChange={(e) => setForm({ ...form, leadSourceId: e.target.value })} className="ciq-input" data-testid="lead-source">
                    <option value="">—</option>
                    {sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </Field>
                <Field label="Status"><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="ciq-input">{STATUSES.filter((s) => s !== "Converted").map((s) => <option key={s}>{s}</option>)}</select></Field>
                <Field label="Rating"><select value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="ciq-input">{RATINGS.map((s) => <option key={s}>{s}</option>)}</select></Field>
              </div>
              <Field label="Estimated Value (INR)"><input type="number" value={form.estimatedValue} onChange={(e) => setForm({ ...form, estimatedValue: e.target.value })} className="ciq-input" data-testid="lead-value" /></Field>
              <Field label="Description"><textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="ciq-input" rows={2} /></Field>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted" data-testid="lead-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="lead-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
